import {
  geoCentroid,
  geoOrthographic,
  geoPath,
  GeoPermissibleObjects,
} from 'd3-geo';
import TimelineLite from 'gsap/TimelineLite';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  GenericGeoJSONFeature, GenericGeoJSONFeatureCollection as FeatureCollection,
} from '../../../types/generic-geojson';
import { Location, LocationLevel } from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { generateStringLocationId } from '../../graphQL/Utils';
import { fetchJSON } from '../../Utils';
import { MapConainer } from '../landing/Grid';
import { primaryColor1 } from '../Utils';

const Container = styled(MapConainer)`
  @media(max-width: 800px) {
    display: none;
  }
`;
const SVG = styled.svg`
`;
const PathEnabled = styled.path`
  &:hover {
    fill: ${primaryColor1};
    cursor: pointer;
  }
`;
const PathDisabled = styled.path`
  &:hover {
    fill: gray;
    cursor: not-allowed;
  }
`;

const query = gql`
  query InCountryProfilesCheck($level: LocationLevel) {
    locations: allLocations(level: $level) {
      id
      isInCountryPages
    }
  }
`;

interface SuccessResponse {
  locations: Array<{
    id: Location['id'],
    isInCountryPages: Location['isInCountryPages'],
  }>;
}

interface Props {
  countryId: string;
  selectCountry: (country: string) => void;
}

interface DOMMeasurement {
  width: number;
  height: number;
}

type Data = FeatureCollection<GeoJSON.MultiPolygon, {}>;

const getRotationCenter =
  (features: Array<GenericGeoJSONFeature<GeoJSON.MultiPolygon, {}>>, countryId: string) => {

  const selectedFeature = features.find(feature => generateStringLocationId(feature.id) === countryId);
  if (selectedFeature === undefined) {
    console.error('Cannot find GeoJSON data for country ' + countryId);
    return [undefined, undefined];
  }
  const [centroidX, centroidY] = geoCentroid(selectedFeature);

  return [- centroidX, - centroidY] as const;
};

const useInCpData = () => {
  const [inCpData, setInCpData] = useState<string[] | undefined>(undefined);
  const response = useQuery<SuccessResponse, {level: LocationLevel}>(query, {variables: {
    level: LocationLevel.country,
  }});
  useEffect(() => {
    if (response && response.data && inCpData === undefined) {
      const {locations} = response.data;
      const filteredLocations: string[] = [];
      locations.forEach(location => {
        if (location.isInCountryPages === true) {
          filteredLocations.push(location.id);
        }
      });
      setInCpData(filteredLocations);
    }
  }, [response, inCpData]);
  return inCpData;
};

const Map = (props: Props) => {
  const {countryId, selectCountry} = props;
  const elRef = useRef<HTMLDivElement | null>(null);
  const [domMeasurements, setDOMMeasurement] = useState<DOMMeasurement | undefined>(undefined);
  const [data, setData] = useState<Data| undefined>(undefined);
  const [rotationCenter, setRotationCenter] = useState<[number, number]>([0, 0]);
  const timelineRef = useRef(new TimelineLite());
  const inCpData = useInCpData();

  useEffect(() => {
    const {current: timeline} = timelineRef;
    if (data !== undefined) {

      const [nextRotationCenterX, nextRotationCenterY] = getRotationCenter(data.features, countryId);
      const [prevRotationCenterX, prevRotationCenterY] = rotationCenter;

      const target = {x: prevRotationCenterX, y: prevRotationCenterY};
      const tweenDuration = 0.5; // in seconds
      timeline.to(target, tweenDuration, {
        x: nextRotationCenterX, y: nextRotationCenterY,
        onUpdate: () => {
          setRotationCenter([target.x, target.y]);
        },
      });
    }
    return () => { timeline.clear(); };
  }, [countryId, data]);

  useEffect(() => {
    const {current: el} = elRef;
    if (el !== null) {
      const {width, height} = el.getBoundingClientRect();
      setDOMMeasurement({
        width: Math.floor(width),
        height: Math.floor(height),
      });
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchJSON<Data>(require(
        '../../sharedData/world_map_naturalearth_ashkyd_clean_geo.json',
      ), true);
      setData(response);
    };
    fetchData();
  }, []);

  let content: React.ReactNode | null;
  if (domMeasurements !== undefined && data !== undefined && rotationCenter !== undefined) {
    const {width, height} = domMeasurements;
    const lesserSide = (width < height) ? width : height;
    const referenceWidth = 415; // in px
    const referenceScale = 207;
    const projectionScale = (lesserSide / referenceWidth) * referenceScale;
    const {features} = data;

    const projection = geoOrthographic()
      .scale(projectionScale)
      .rotate(rotationCenter)
      .translate([width / 2, height / 2])
      .clipAngle(90);
    const generatePath = geoPath().projection(projection);
    const pathList = features.map(feature => {
      const path = generatePath(feature as any as GeoPermissibleObjects)!;
      return {
        id: generateStringLocationId(feature.id),
        path,
      };
    });
    const pathListElms = pathList.map(({id, path}, index) => {
      const fill = (id === countryId) ? primaryColor1 : '#fff';
      const fillOpacity = (id === countryId) ? 1 : 0.4;
      const isInCp = inCpData !== undefined && inCpData.includes(id);
      const toggleCountry = () => {
        if (isInCp) {
          selectCountry(id);
        }
      };
      const Path = isInCp ? PathEnabled : PathDisabled;
      return (
        <Path
          d={path} key={`globe-path-${id}-${index}`}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={'#333'}
          strokeWidth={0.5}
          onClick={toggleCountry}
        />
      );
    });
    content = (
      <SVG width={width} height={height}>
        <circle
          cx={width / 2}
          cy={height / 2}
          r={lesserSide / 2}
          stroke='#333'
          strokeWidth={0.5}
          fill='white'
          fillOpacity={0.2}
        />
        {pathListElms}
      </SVG>
    );
  } else {
    content = null;
  }
  return (
    <Container ref={elRef}>
      {content}
    </Container>
  );
};

export default Map;
